(() => {
  if (customElements.get('product-activity-bar')) return;

  class ProductActivityBar extends HTMLElement {
    connectedCallback() {
      if (this.dataset.activityInitialized === 'true') return;
      this.dataset.activityInitialized = 'true';

      this.viewport = this.querySelector('[data-activity-viewport]');
      this.track = this.querySelector('[data-activity-track]');
      this.items = Array.from(this.querySelectorAll('[data-activity-item]'));
      this.currentIndex = 0;
      this.itemHeight = 0;
      this.timer = null;
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.autoplay = this.dataset.autoplay === 'true' && this.items.length > 1 && !this.reduceMotion;
      this.interval = Math.max(parseInt(this.dataset.interval, 10) || 4000, 1500);

      if (!this.viewport || !this.track || this.items.length === 0) {
        this.style.display = 'none';
        return;
      }

      this.handleResize = this.debounce(() => this.measure(), 120);
      this.handleVisibility = () => {
        if (!this.autoplay) return;
        if (document.hidden) this.stop();
        else this.start();
      };

      requestAnimationFrame(() => {
        this.measure();
        this.goTo(0, false);
        if (this.autoplay) this.start();
      });

      window.addEventListener('resize', this.handleResize, { passive: true });
      document.addEventListener('visibilitychange', this.handleVisibility);

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.handleResize());
        this.resizeObserver.observe(this);
      }
    }

    disconnectedCallback() {
      this.stop();
      window.removeEventListener('resize', this.handleResize);
      document.removeEventListener('visibilitychange', this.handleVisibility);
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    measure() {
      if (!this.viewport || !this.track || this.items.length === 0) return;

      const previousTransition = this.track.style.transition;
      this.track.style.transition = 'none';
      this.track.style.transform = 'translate3d(0, 0, 0)';
      this.items.forEach((item) => {
        item.style.height = 'auto';
      });

      let maxHeight = 0;
      this.items.forEach((item) => {
        maxHeight = Math.max(maxHeight, item.scrollHeight);
      });

      this.itemHeight = Math.max(maxHeight, 1);
      this.viewport.style.height = `${this.itemHeight}px`;

      const align = getComputedStyle(this).textAlign;
      this.items.forEach((item) => {
        item.style.height = `${this.itemHeight}px`;
        item.style.display = 'flex';
        item.style.alignItems = 'center';
        if (align === 'center') item.style.justifyContent = 'center';
        else if (align === 'right' || align === 'end') item.style.justifyContent = 'flex-end';
        else item.style.justifyContent = 'flex-start';
      });

      this.track.style.transform = `translate3d(0, -${this.currentIndex * this.itemHeight}px, 0)`;

      requestAnimationFrame(() => {
        this.track.style.transition = previousTransition || 'transform 450ms cubic-bezier(0.22, 1, 0.36, 1)';
      });
    }

    goTo(index, animate = true) {
      if (!this.itemHeight || this.items.length === 0) return;

      this.currentIndex = (index + this.items.length) % this.items.length;
      this.track.style.transition = animate
        ? 'transform 450ms cubic-bezier(0.22, 1, 0.36, 1)'
        : 'none';
      this.track.style.transform = `translate3d(0, -${this.currentIndex * this.itemHeight}px, 0)`;

      this.items.forEach((item, itemIndex) => {
        item.setAttribute('aria-hidden', itemIndex === this.currentIndex ? 'false' : 'true');
      });
    }

    next() {
      this.goTo(this.currentIndex + 1, true);
    }

    start() {
      this.stop();
      if (!this.autoplay || document.hidden) return;
      this.timer = window.setInterval(() => this.next(), this.interval);
    }

    stop() {
      if (this.timer) {
        window.clearInterval(this.timer);
        this.timer = null;
      }
    }

    debounce(fn, wait) {
      let timeout;
      return (...args) => {
        window.clearTimeout(timeout);
        timeout = window.setTimeout(() => fn.apply(this, args), wait);
      };
    }
  }

  customElements.define('product-activity-bar', ProductActivityBar);
})();
